import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flashlight, RotateCcw, X, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import HttpService from '@/shared/services/http.service';
import { API_CONFIG } from '@/shared/constants/api';
import { useNavigate } from 'react-router-dom';
import { logEvent } from '@/analytics';

// Extend MediaTrackConstraints to include torch property
declare global {
  interface MediaTrackConstraints {
    torch?: boolean;
  }
  interface MediaTrackConstraintSet {
    torch?: boolean;
  }
  interface MediaTrackCapabilities {
    torch?: boolean;
  }
}

interface PackageData {
  id: string;
  packageNumber: string;
  slotCode: string;
  barcodes: string[];
  name?: string;
}

interface OutboundScannerProps {
  onScanSuccess: (imageUrl: string, packageData: PackageData | null) => void;
  onError: (error: string) => void;
}

const OutboundScanner = ({ onScanSuccess, onError }: OutboundScannerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [currentCamera, setCurrentCamera] = useState<'environment' | 'user'>('environment');
  const [torchOn, setTorchOn] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // const [showManualInput, setShowManualInput] = useState(false);
  // const [manualName, setManualName] = useState('');

  const startCamera = useCallback(
    async (camera: 'environment' | 'user' = 'environment') => {
      try {
        setCameraError(null);
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: camera,
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet],
          },
          audio: false,
        });

        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (err) {
        const errorMessage = t('camera_access_denied_short');
        setCameraError(errorMessage);
        onError(errorMessage);
      }
    },
    [onError],
  );

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setTorchOn(false);
    setCameraError(null);
  }, [stream]);

  const handleCapture = async () => {
    if (!stream || !videoRef.current || !canvasRef.current) return;

    setIsUploading(true);
    setUploadProgress(0);

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (context) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        async (blob) => {
          if (blob) {
            const reader = new FileReader();
            reader.onload = (ev) => setCapturedImage(ev.target?.result as string);
            reader.readAsDataURL(blob);

            // Stop camera after capture
            stopCamera();

            const uploadStartTime = performance.now();

            try {
              // Step 1: Get presigned URL
              setUploadProgress(25);

              const presignedResponse = await HttpService.post(API_CONFIG.path.presignedUrl, {
                fileType: 'image/jpeg',
                extension: 'jpg',
              });

              if (!presignedResponse.uploadUrl) {
                throw new Error(t('failed_to_get_presigned_url'));
              }

              setUploadProgress(50);
              const uploadUrl = presignedResponse.uploadUrl;
              setUploadProgress(75);

              // Step 2: Upload image to presigned URL
              const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: blob,
                headers: {
                  'Content-Type': 'image/jpeg',
                },
              });

              const uploadDurationSec = Number(((performance.now() - uploadStartTime) / 1000).toFixed(2));
              logEvent('image_upload_duration', {
                flow: 'outbound',
                durationSec: uploadDurationSec,
                duration_bucket: uploadDurationSec.toFixed(2),
                success: uploadResponse.ok ? 'true' : 'false',
              });

              if (uploadResponse.ok) {
                setUploadProgress(100);
                const imageUrl = uploadUrl.split('?')[0];

                // Step 3: Call retrieve API with imageUrl
                try {
                  const retrieveResponse = await HttpService.post(API_CONFIG.path.retrievePackage, {
                    imageUrl: imageUrl,
                  });

                  // Call success callback with image URL and package data
                  onScanSuccess(imageUrl, retrieveResponse);
                } catch (retrieveError: unknown) {
                  console.error('❌ Error calling retrieve API:', retrieveError);

                  // Check if it's a 400 or 404 status code
                  const error = retrieveError as {
                    response?: { status?: number };
                    status?: number;
                  };
                  if (
                    error?.response?.status === 400 ||
                    error?.status === 400 ||
                    error?.response?.status === 404 ||
                    error?.status === 404
                  ) {
                    // Navigate to outbound flow with manual input parameter
                    navigate('/outbound?manual_input=true');
                    return; // Don't call success yet, let OutboundFlow handle manual input
                  }

                  // Still call success with imageUrl even if retrieve fails for other errors
                  onScanSuccess(imageUrl, null);
                }
              } else {
                throw new Error(t('upload_failed_retry'));
              }
            } catch (error) {
              const uploadDurationSec = Number(((performance.now() - uploadStartTime) / 1000).toFixed(2));
              logEvent('image_upload_duration', {
                flow: 'outbound',
                durationSec: uploadDurationSec,
                duration_bucket: uploadDurationSec.toFixed(2),
                success: 'false',
              });
              console.error('❌ Error during upload process:', error);
              setUploadError(t('failed_to_process_image'));
            } finally {
              setIsUploading(false);
            }
          }
        },
        'image/jpeg',
        0.95,
      );
    }
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setUploadError(null);
    startCamera(currentCamera);
  };

  const clearCameraError = () => {
    setCameraError(null);
  };

  // Start camera when component mounts
  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const switchCamera = async () => {
    stopCamera();
    const newCamera = currentCamera === 'environment' ? 'user' : 'environment';
    setCurrentCamera(newCamera);
    setTimeout(() => startCamera(newCamera), 100);
  };

  const toggleTorch = async () => {
    if (!stream) return;

    try {
      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack) return;

      const capabilities = videoTrack.getCapabilities() as MediaTrackCapabilities;

      if (capabilities.torch) {
        await videoTrack.applyConstraints({
          advanced: [{ torch: !torchOn }],
        });
        setTorchOn(!torchOn);
      } else {
        onError(t('flashlight_not_available_device'));
      }
    } catch (error) {
      console.error('Torch toggle error:', error);
      onError(t('failed_to_toggle_torch'));
    }
  };

  return (
    <div className="relative w-full h-[calc(100vh-97px)] overflow-hidden rounded-lg flex flex-col">
      {/* Instructions banner */}
      <div className="relative z-20 pointer-events-none mb-4">
        <p className="text-foreground/90 text-sm font-medium mb-1">{t('capture_the_complete_parcel')}</p>
        <p className="text-foreground/70 text-xs">{t('ensure_all_text_and')}</p>
      </div>

      {/* Camera area */}
      <div className="relative h-[calc(100%-56px)] overflow-hidden rounded-lg">
        {/* Camera error display */}
        {cameraError ? (
          <div className="w-full h-full bg-gray-100 flex items-center justify-center rounded-lg">
            <div className="text-center p-6 max-w-sm">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <div className="text-left text-sm text-gray-600 mb-5 space-y-1">
                <p className="text-red-600 font-semibold text-lg mb-2">{t('camera_is_blocked_by')}</p>
                <p className="text-red-600 text-sm mb-4 text-center">{cameraError || t('permission_denied')}</p>
                <p className="mt-2 font-medium">{t('how_to_fix')}</p>
                <p>{t('1_click_icon_in')}</p>
                <p>
                  {t('2_set')} <b>{t('camera_allow')}</b>
                </p>
                <p>{t('3_reload_this_page')}</p>
              </div>

              <div className="flex flex-col gap-2">
                <Button onClick={clearCameraError} className="w-full">
                  {t('continue_anyway')}
                </Button>
                <Button variant="outline" onClick={() => window.location.reload()} className="w-full">
                  {t('reload_page')}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Camera capture state */}
            {!capturedImage && (
              <>
                <video
                  ref={videoRef}
                  className="absolute top-0 left-0 w-full h-full object-cover"
                  autoPlay
                  playsInline
                />

                {/* Camera controls */}
                <div className="absolute top-20 right-1/2 translate-x-1/2 flex gap-2 pointer-events-auto">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white/70 hover:bg-gray-700/50 bg-black/20 backdrop-blur-sm"
                    onClick={toggleTorch}
                  >
                    <Flashlight className={`w-6 h-6 ${torchOn ? 'text-yellow-400' : ''}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white/70 hover:bg-gray-700/50 bg-black/20 backdrop-blur-sm"
                    onClick={switchCamera}
                  >
                    <RotateCcw className="w-6 h-6" />
                  </Button>
                </div>

                {/* Capture button */}
                <div className="absolute bottom-20 left-1/2 -translate-x-1/2 pointer-events-auto flex flex-col items-center w-[80%]">
                  <Button
                    className="w-16 h-16 mx-auto mb-2 rounded-full bg-blue-500 flex items-center justify-center"
                    onClick={handleCapture}
                  >
                    <Camera className="!w-8 !h-8 text-white" />
                  </Button>
                  <p className="text-white/80 text-sm text-center">{t('place_entire_invoice_on')}</p>
                </div>
              </>
            )}

            {capturedImage && (isUploading || uploadError) && (
              <div className="relative h-full rounded-lg">
                {/* Captured Image */}
                <img
                  src={capturedImage}
                  alt={t('captured_invoice')}
                  className="absolute top-0 left-0 w-full h-full object-cover"
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="bg-white rounded-xl p-6 max-w-sm w-full m-4">
                    <div className="flex flex-col items-center gap-3">
                      {/* Uploading */}
                      {isUploading && (
                        <>
                          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                          <p className="text-gray-900 font-medium">{t('processing_image')}</p>

                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>

                          <p className="text-gray-600 text-sm">
                            {uploadProgress}
                            {t('complete')}
                          </p>
                        </>
                      )}

                      {/* Error */}
                      {!isUploading && uploadError && (
                        <>
                          <p className="text-red-600 font-medium text-center">{uploadError}</p>
                          <Button
                            onClick={() => {
                              setUploadError(null);
                              handleRetake();
                            }}
                            className="w-full"
                          >
                            {t('retake_photo')}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default OutboundScanner;
